import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { DEPARTMENTS, PRIORITIES, deptLabel } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, FileText, Loader2 } from "lucide-react";

export default function NewComplaintPage() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [form, setForm] = useState({
    applicant_name: "",
    mobile: "",
    subject: "",
    description: "",
    department: "",
    priority: "Medium",
  });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function update(field, val) {
    setForm((f) => ({ ...f, [field]: val }));
  }

  async function onFilePick(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((prev) => [...prev, data]);
      } catch (err) {
        toast.error(`Upload failed: ${apiErrorMessage(err)}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  }

  function removeAttachment(id) {
    setAttachments((prev) => prev.filter((f) => f.id !== id));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.department) {
      toast.error("Department is required");
      return;
    }
    if (!/^\d{10}$/.test(form.mobile)) {
      toast.error("Enter a 10-digit mobile number");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/complaints", form);
      // If attachments, add them to the initial update
      if (attachments.length > 0) {
        const updates = await api.get(`/complaints/${data.id}/updates`);
        const initialUpdate = updates.data.find((u) => u.update_number === 1);
        if (initialUpdate) {
          for (const att of attachments) {
            const fd = new FormData();
            fd.append("file_id", att.id);
            await api.post(
              `/complaints/${data.id}/updates/${initialUpdate.id}/media`,
              fd
            );
          }
        }
      }
      toast.success(`Complaint ${data.complaint_no} submitted`);
      navigate(`/complaints/${data.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-2">
          New Complaint
        </div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
          Register a citizen complaint
        </h1>
        <p className="text-slate-500 text-sm mt-2">
          All fields marked with <span className="text-red-600">*</span> are
          mandatory. A unique complaint number will be generated automatically.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="bg-white border border-slate-200 rounded-lg p-6 sm:p-8 space-y-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-sm font-semibold text-slate-700">
              Applicant Name <span className="text-red-600">*</span>
            </Label>
            <Input
              data-testid="new-applicant-name"
              value={form.applicant_name}
              onChange={(e) => update("applicant_name", e.target.value)}
              required
              className="mt-1.5 h-11"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700">
              Mobile Number <span className="text-red-600">*</span>
            </Label>
            <Input
              data-testid="new-mobile"
              value={form.mobile}
              onChange={(e) =>
                update("mobile", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))
              }
              required
              placeholder="10-digit mobile"
              className="mt-1.5 h-11 font-mono-cms"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-semibold text-slate-700">
            Subject <span className="text-red-600">*</span>
          </Label>
          <Input
            data-testid="new-subject"
            value={form.subject}
            onChange={(e) => update("subject", e.target.value)}
            required
            className="mt-1.5 h-11"
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-slate-700">
            Complaint Description <span className="text-red-600">*</span>
          </Label>
          <Textarea
            data-testid="new-description"
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            required
            rows={5}
            className="mt-1.5"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-sm font-semibold text-slate-700">
              Department (विभाग) <span className="text-red-600">*</span>
            </Label>
            <Select
              value={form.department}
              onValueChange={(v) => update("department", v)}
            >
              <SelectTrigger
                data-testid="new-department"
                className={`mt-1.5 h-11 ${lang === "hi" ? "font-devanagari" : ""}`}
              >
                <SelectValue placeholder="Select a department" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {DEPARTMENTS.map((d) => (
                  <SelectItem
                    key={d}
                    value={d}
                    className={lang === "hi" ? "font-devanagari" : ""}
                    data-testid={`dept-option-${d}`}
                  >
                    {deptLabel(d, lang)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700">
              Priority
            </Label>
            <Select
              value={form.priority}
              onValueChange={(v) => update("priority", v)}
            >
              <SelectTrigger data-testid="new-priority" className="mt-1.5 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p} data-testid={`priority-option-${p}`}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Attachments */}
        <div>
          <Label className="text-sm font-semibold text-slate-700">
            Attach Documents (Optional)
          </Label>
          <div className="mt-2 border-2 border-dashed border-slate-300 rounded-md p-6 text-center hover:border-orange-400 transition-colors">
            <input
              type="file"
              id="attach"
              data-testid="new-attach"
              multiple
              accept="image/*,video/*,application/pdf,.doc,.docx"
              onChange={onFilePick}
              className="hidden"
            />
            <label
              htmlFor="attach"
              className="cursor-pointer inline-flex flex-col items-center gap-2"
            >
              {uploading ? (
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
              ) : (
                <Upload className="w-6 h-6 text-slate-400" />
              )}
              <span className="text-sm text-slate-600 font-semibold">
                Click to upload
              </span>
              <span className="text-xs text-slate-500">
                Images, PDFs, videos & documents · max 60MB each
              </span>
            </label>
          </div>
          {attachments.length > 0 && (
            <div className="mt-3 space-y-2">
              {attachments.map((a) => (
                <div
                  key={a.id}
                  data-testid={`attach-${a.id}`}
                  className="flex items-center justify-between px-3 py-2 rounded-md border border-slate-200 bg-slate-50"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    <span className="text-sm truncate">
                      {a.original_filename}
                    </span>
                    <span className="text-xs text-slate-400 font-mono-cms">
                      {(a.size / 1024).toFixed(1)}KB
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.id)}
                    className="text-slate-400 hover:text-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            data-testid="new-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            data-testid="new-submit"
            disabled={submitting || uploading}
            className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 h-11"
          >
            {submitting ? "Submitting..." : "Submit Complaint"}
          </Button>
        </div>
      </form>
    </div>
  );
}
